import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { toMatchKey } from '@/shared/variantMapping/matchKey';
import { loadVariantLinks } from '@/shared/variantMapping/variantLinks';

export interface OpenPoLine {
  purchaseOrderId: string;
  organisationId: string;
  status: string | null;
  immutableAt: string | null;
}

interface PurchaseOrderRef {
  organisation_id: string;
  status: string | null;
  immutable_at: string | null;
}

interface LineItemRow {
  purchase_order_id: string;
  purchase_orders: PurchaseOrderRef | PurchaseOrderRef[] | null;
}

/** Open purchase orders are not archived and not locked. */
export function openPurchaseOrderIds(lines: OpenPoLine[], organisationId: string): string[] {
  const ids = new Set<string>();
  for (const line of lines) {
    if (line.organisationId !== organisationId) continue;
    if (line.status === 'archived' || line.immutableAt) continue;
    if (!line.purchaseOrderId) continue;
    ids.add(line.purchaseOrderId);
  }
  return [...ids];
}

/**
 * Every spelling of each code the `sku` filter has to be asked for.
 *
 * The database filter matches `sku` exactly, while matching treats a composed
 * and a decomposed accent as the same code, so asking for only the spelling the
 * caller happened to carry skips the PO line that stores the other one.
 *
 * Both forms are always returned, never just the one the incoming code is not
 * already in. A CSV normally carries NFC, so deriving the extra spelling only
 * when the code arrived as NFD left the usual direction -- NFC in hand, NFD on
 * the PO line -- as the one never queried, and that order went unrecalculated.
 */
export function codeSpellings(skus: string[]): string[] {
  return [...new Set(
    skus.flatMap((sku) => {
      const trimmed = sku.trim();
      if (!trimmed) return [];
      return [trimmed, trimmed.normalize('NFC'), trimmed.normalize('NFD')];
    }),
  )];
}

function purchaseOrderOf(row: LineItemRow): PurchaseOrderRef | null {
  if (Array.isArray(row.purchase_orders)) return row.purchase_orders[0] ?? null;
  return row.purchase_orders;
}

export interface RecalculateOptions {
  /**
   * FIG-438 rule 4b: re-run PO code matching as part of this recalculation.
   * Only a mapping CSV upload sets this. Every other caller leaves it off, so a
   * recalculation cannot quietly change which Variant a PO line is matched to.
   */
  matchPoCodes?: boolean;
}

/** Recalculate funding on open POs that contain one of these codes. Failures are logged. */
export async function recalculateOpenPosForSkus(
  organisationId: string,
  skus: string[],
  options: RecalculateOptions = {},
): Promise<void> {
  // Every caller discards this promise with `void`, after the screen has
  // already told the user their change saved. A throw anywhere below would
  // surface as an unhandled rejection rather than as anything actionable, so
  // the whole body is contained and reported instead.
  try {
    await recalculateOpenPosForSkusUnsafe(organisationId, skus, options);
  } catch (error) {
    console.error('Failed to recalculate open purchase order funding', organisationId, error);
  }
}

async function recalculateOpenPosForSkusUnsafe(
  organisationId: string,
  skus: string[],
  options: RecalculateOptions,
): Promise<void> {
  const values = codeSpellings(skus);
  if (values.length === 0) return;

  const pageSize = 1000;
  // `in` goes into the query string, so a whole mapping CSV's worth of values in
  // one request can exceed the URL limit a server or proxy accepts. The import
  // would still have succeeded, and the refresh would fail silently.
  const filterChunkSize = 100;
  const rows: LineItemRow[] = [];
  for (let start = 0; start < values.length; start += filterChunkSize) {
    const chunk = values.slice(start, start + filterChunkSize);
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await supabase
        .from('purchase_order_line_items')
        .select('purchase_order_id, purchase_orders!inner(organisation_id, status, immutable_at)')
        .eq('purchase_orders.organisation_id', organisationId)
        .in('sku', chunk)
        .order('id', { ascending: true })
        .range(from, from + pageSize - 1);

      if (error) {
        console.error('Failed to find open purchase orders for variant funding', error);
        return;
      }

      const page = (data ?? []) as LineItemRow[];
      rows.push(...page);
      if (page.length < pageSize) break;
    }
  }

  const lines: OpenPoLine[] = rows.map((row) => {
    const purchaseOrder = purchaseOrderOf(row);
    return {
      purchaseOrderId: row.purchase_order_id,
      organisationId: purchaseOrder?.organisation_id ?? '',
      status: purchaseOrder?.status ?? null,
      immutableAt: purchaseOrder?.immutable_at ?? null,
    };
  });

  await Promise.all(openPurchaseOrderIds(lines, organisationId).map(async (purchaseOrderId) => {
    try {
      await invokeFunction(supabase, 'calculate-po-funding', {
        body: { purchaseOrderId, organisationId, matchPoCodes: options.matchPoCodes === true },
      });
    } catch (recalcError) {
      // invokeFunction throws instead of returning an error, and every caller
      // discards this promise with `void`, so without this catch a failed
      // refresh becomes an unhandled rejection -- after the screen has already
      // told the user the mapping saved. Name the order that still needs one.
      console.error('Failed to recalculate purchase order funding', purchaseOrderId, recalcError);
    }
  }));
}

interface LegacyPairRow {
  primary_value: string | null;
  alias_value: string | null;
}

/** `in` goes into the query string, so values are asked for in batches. */
const FILTER_CHUNK = 100;
const LEGACY_PAGE_SIZE = 1000;

function chunk<T>(values: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let start = 0; start < values.length; start += size) out.push(values.slice(start, start + size));
  return out;
}

/**
 * Every code that shares a Variant — or a legacy primary — with one of these.
 *
 * Funding for a line is computed from its whole Variant group, so a code's
 * figures move when a *sibling* identifier joins or leaves that Variant, even
 * though the code itself never appears in the file that caused it. Refreshing
 * only the values printed in a mapping CSV therefore left sibling orders on
 * figures that still counted stock and sales the group no longer has.
 *
 * Call it before the import to learn the group as it stands, and again after to
 * learn the group the import produced; a code that left one group and a code
 * that stayed behind are both only visible in one of the two.
 *
 * The links come from `loadVariantLinks`, which pages and merges a clone's
 * inherited links the way funding resolves them — the group is then worked out
 * in memory, so no row cap can silently truncate it.
 *
 * Best-effort: any failure returns the values it was given rather than throwing,
 * since the pre-import caller runs before the import is sent.
 */
export async function identifierGroupValues(
  organisationId: string,
  values: string[],
): Promise<string[]> {
  const wanted = codeSpellings(values);
  if (wanted.length === 0) return [];
  try {
    return await groupValuesUnsafe(organisationId, wanted);
  } catch (error) {
    console.error('Failed to expand the Variant group for funding refresh', error);
    return wanted;
  }
}

async function groupValuesUnsafe(organisationId: string, wanted: string[]): Promise<string[]> {
  const found = new Set<string>(wanted);

  // Matched on the same key the rest of Variant Mapping uses, so a composed and
  // a decomposed spelling of one code are the same identifier here too.
  const soughtKeys = new Set(wanted.map((value) => toMatchKey(value)));

  const links = await loadVariantLinks(organisationId);
  const mastersWanted = new Set<string>();
  for (const link of links) {
    if (!link.master_product_id || !link.identifier_value) continue;
    if (soughtKeys.has(toMatchKey(link.identifier_value))) mastersWanted.add(link.master_product_id);
  }
  for (const link of links) {
    if (!link.identifier_value || !link.master_product_id) continue;
    if (mastersWanted.has(link.master_product_id)) found.add(link.identifier_value);
  }

  for (const value of await legacyGroupValues(organisationId, wanted)) found.add(value);

  return [...found];
}

/** Legacy product_mappings are alias -> primary and fund as one group too. */
async function legacyGroupValues(organisationId: string, wanted: string[]): Promise<string[]> {
  const mappings = supabase as unknown as {
    from: (table: string) => {
      select: (cols: string) => {
        eq: (col: string, val: string) => {
          in: (col: string, vals: string[]) => {
            order: (col: string, opts: { ascending: boolean }) => {
              range: (from: number, to: number) => Promise<{ data: unknown; error: unknown }>;
            };
          };
        };
      };
    };
  };

  const found: string[] = [];
  // One `in` per column rather than a hand-built `or(...)` filter string. The
  // filter had to quote each value itself, which escaped `"` but not `\`, so a
  // value carrying a backslash could break out of the quoting (CodeQL
  // js/incomplete-sanitization). Letting the client encode an `in` list removes
  // the quoting problem rather than patching it.
  for (const column of ['primary_value', 'alias_value'] as const) {
    for (const part of chunk(wanted, FILTER_CHUNK)) {
      for (let from = 0; ; from += LEGACY_PAGE_SIZE) {
        const { data, error } = await mappings
          .from('product_mappings')
          .select('primary_value, alias_value')
          .eq('organisation_id', organisationId)
          .in(column, part)
          .order('id', { ascending: true })
          .range(from, from + LEGACY_PAGE_SIZE - 1);
        if (error) {
          console.error('Failed to read legacy aliases for funding refresh', error);
          break;
        }
        const page = (data ?? []) as LegacyPairRow[];
        for (const row of page) {
          if (row.primary_value) found.push(row.primary_value);
          if (row.alias_value) found.push(row.alias_value);
        }
        if (page.length < LEGACY_PAGE_SIZE) break;
      }
    }
  }
  return found;
}
